import api from '@api'
import { QrCodeFileFormat, QrCodeOptions } from '@api/QRCode/download'
import { ControlGroup, Intent } from '@blueprintjs/core'
import track from '@track'
import Event from '@track/Event'
import { Button, Dialog, Flex, IconButton, LogoSpinner, Toaster } from '@ui'
import QrImageContent from 'components/BuildingManagement/QRCode/QrImageContent'
import QrLabelContent from 'components/BuildingManagement/QRCode/QrLabelContent'
import React, { useState } from 'react'

type QRCodePopupProps = {
  isQRCodePopoverOpen: boolean
  setIsQRCodePopoverOpen: (value: boolean) => void
  spaceIds: string[]
}

export const QRCodeDialog = ({
  isQRCodePopoverOpen,
  setIsQRCodePopoverOpen,
  spaceIds,
}: QRCodePopupProps) => {
  const [isTransparent, setIsTransparent] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [type, setType] = useState<string>('image')
  const [color, setColor] = useState('#000')
  const [fileFormat, setFileFormat] = useState<QrCodeFileFormat>('PNG')
  const [messageMode, setMessageMode] = useState<null | string>(null)
  const [size, setSize] = useState<{ value: string; title: string }>({
    value: 'small',
    title: 'Small',
  })

  const onTypeChange = (type: string) => {
    if (type === 'label') {
      setIsTransparent(false)
    }
    setType(type)
  }

  const onClose = () => setIsQRCodePopoverOpen(false)

  const onFileformatChange = (fileFormat: QrCodeFileFormat) => {
    if (fileFormat === 'PDF' || fileFormat === 'JPG') {
      setIsTransparent(false)
    }
    setFileFormat(fileFormat)
  }

  const onDownload = () => {
    setProcessing(true)
    const input: QrCodeOptions = {
      spaceIds: spaceIds,
      floorIds: [],
      generatorOptions: {
        fileFormat,
        size: size.value,
        color,
        type,
        is_transparent: isTransparent,
      },
    }

    api.QRCode.download(input)
      .then((message) => {
        onClose()
        setMessageMode(message)
      })
      .catch((error) => Toaster.fail({ error }))
      .finally(() => setProcessing(false))
    track.downloadQRCode({
      eventName: Event.DOWNLOAD_QR_CODE,
      props: {
        component: 'Right Drawer',
        actionName: 'Download QR',
      },
    })
  }

  const onMessageModeClose = () => {
    setMessageMode(null)
  }

  return messageMode ? (
    <Dialog
      isOpen
      title={<div style={{ marginLeft: '15px' }}>Download QR Codes</div>}
      style={{ width: 800 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      onClose={onMessageModeClose}
      toolbar={<DialogToolbar onClose={onClose} />}
      body={
        <div style={{ marginLeft: '20px' }}>
          <Flex align="center" justify="center" style={{ paddingBottom: 8 }}>
            <LogoSpinner size={30} />
          </Flex>
          <div style={{ margin: '25px 0px' }}>{messageMode}</div>
          <section className={'flex justify-end'}>
            <Button style={{ marginLeft: 18 }} text="OK" onClick={onMessageModeClose} />
          </section>
        </div>
      }
    />
  ) : (
    <Dialog
      isOpen={isQRCodePopoverOpen}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <DialogToolbar
            onClose={onClose}
            containerStyle={{ backgroundColor: '#F1F3F4', top: 5 }}
            closeButtonStyle={{ backgroundColor: '#F1F3F4' }}
            containerClassName="big-header"
          />
        </div>
      }
      className="gray-header"
      style={{ width: 800 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      onClose={onClose}
      body={
        <div style={{ paddingLeft: '20px', paddingTop: '16px' }}>
          <ControlGroup style={{ marginBottom: 8 }}>
            <Button
              style={{ borderRadius: '4px 0 0 4px', width: '150px', height: '32px' }}
              onClick={() => onTypeChange('image' as string)}
              intent={type === 'image' ? Intent.PRIMARY : Intent.NONE}
              text="QR Code Image"
            />
            <Button
              blue
              style={{ borderRadius: '0 4px 4px 0', width: '153px', height: '32px' }}
              onClick={() => onTypeChange('label' as string)}
              intent={type === 'label' ? Intent.PRIMARY : Intent.NONE}
              text="QR Code Label"
            />
          </ControlGroup>
          {type === 'image' && (
            <QrImageContent
              size={size}
              type={type}
              color={color}
              fileFormat={fileFormat}
              onFileFormatChange={onFileformatChange}
              isTransparent={isTransparent}
              isProcessing={processing}
              onSizeChange={setSize}
              onDownload={onDownload}
              onChangeColor={(color) => setColor(color)}
              onTransparentChange={() => setIsTransparent((value) => !value)}
              onCancel={() => onClose()}
            />
          )}
          {type === 'label' && (
            <QrLabelContent
              color={color}
              type={type}
              size={size}
              isProcessing={processing}
              onSizeChange={setSize}
              onDownload={onDownload}
              onChangeColor={setColor}
              onCancel={() => onClose()}
              onFileFormatChange={onFileformatChange}
            />
          )}
        </div>
      }
    />
  )
}

function DialogToolbar({
  onClose,
  closeButtonStyle,
  containerStyle,
  containerClassName,
}: Readonly<{
  onClose?: () => void
  closeButtonStyle?: React.CSSProperties
  containerStyle?: React.CSSProperties
  containerClassName?: string
}>) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'flex-end', ...containerStyle }}
      className={`dialog-buttons ${containerClassName ? containerClassName : ''}`}>
      <IconButton
        data-button="close_dialog"
        style={closeButtonStyle}
        minimal
        tooltip="Close"
        iconName="times"
        onClick={onClose}
      />
    </div>
  )
}
